import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { ExamsApi, Topic, TopicsView } from '../api/exams'
import { TopicDrawer } from './TopicDrawer'
import { TopicMembership } from './TopicMembership'
import { TopicsUpload } from './TopicsUpload'

/**
 * Exam > Topics: the Exam's Topics, their Subtopics, and which Session examines which.
 *
 * A Topic is the Exam's and never another's, and a Session names the Topics it examines --
 * membership only, nothing weighted. A Mock is composed outside the platform, so nothing here
 * says how many of its Questions a Topic gets. The one rule is that every Topic sits under at
 * least one Session and every Session examines at least one Topic (app:R83).
 *
 * EDITED AS A DRAFT, SAVED WHOLE. Moving a Topic from one Paper to another is two edits the
 * rule above would refuse one at a time, so the screen keeps every change -- a Topic added or
 * edited in the drawer, its Sections ticked there -- in one draft, and writes the lot with
 * one Save.
 *
 * Nothing is filed under a Topic: the Bank cites Topic Codes as text, and how the Bank falls
 * across this list is the Questions area's Coverage screen.
 */
export function Topics({ exams, exam }: { exams: ExamsApi; exam: string }) {
  const client = useQueryClient()
  const read = useQuery({ queryKey: ['topics', exam], queryFn: () => exams.topics(exam) })
  // The Format, for the Components and Sections the upload's prompt names.
  const { data: catalogue } = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  const [draft, setDraft] = useState<Document | null>(null)
  const [opened, setOpened] = useState<Topic | null>(null)
  const [adding, setAdding] = useState(false)

  const columns = useMemo(() => (read.data ? columnsOf(read.data) : []), [read.data])
  const document = draft ?? (read.data ? documentOf(read.data) : null)

  // Where the rule does not hold. None of it matters while there are no Topics at all: an Exam
  // whose specification is not entered yet has nothing to place.
  const listed = document !== null && document.topics.length > 0
  const unplaced = listed ? unplacedOf(document) : []
  const empty = listed ? emptyOf(document, columns) : []
  const broken = unplaced.length > 0 || empty.length > 0

  const save = useMutation({
    mutationFn: (written: Document) => exams.setTopics(exam, written),
    onSuccess: (written) => {
      setDraft(null)
      client.setQueryData(['topics', exam], written)
    },
  })

  const failure = read.error?.message ?? save.error?.message ?? null

  return (
    <>
      {/* Half and half, as Questions Management is: the heading names the screen and the one
          thing you come here to do first -- get the published specification in -- sits beside
          it rather than under the Topic list it produces. It stacks on a narrow screen. */}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <h2 className="font-heading text-h2 font-bold text-heading">Topics</h2>
        <TopicsUpload
          exams={exams}
          exam={exam}
          components={catalogue?.find((entry) => entry.code === exam)?.components ?? []}
          held={read.data?.topics.length ?? 0}
        />
      </div>

      {failure && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {failure}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {broken && (
          <p className="text-red-700">
            {[
              ...unplaced.map((topic) => `${topic.code} is under no Session`),
              ...empty.map((column) => `${column.label} examines no Topic`),
            ].join('; ')}
            .
          </p>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-4">
          {draft && (
            <button
              type="button"
              className={quiet}
              disabled={save.isPending}
              onClick={() => {
                setDraft(null)
                save.reset()
              }}
            >
              Discard changes
            </button>
          )}

          <button
            type="button"
            className={quiet}
            disabled={!document}
            onClick={() => {
              setAdding(true)
              setOpened({ code: '', name: '', subtopics: [] })
            }}
          >
            <Plus aria-hidden size={16} /> Add a Topic
          </button>

          <button
            type="button"
            className={loud}
            disabled={!draft || broken || save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            {save.isPending ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {document && document.topics.length === 0 && !draft && (
        <p className="mt-4 text-muted">
          No Topics for {exam} yet. Upload the specification, or add them one at a time.
        </p>
      )}

      {read.isPending && <p className="mt-4 text-muted">Loading the Topics...</p>}

      {document && document.topics.length > 0 && (
        <TopicMembership
          sections={sectionsOf(document, columns, read.data)}
          unplaced={unplaced}
          onOpen={(code) => {
            setAdding(false)
            setOpened(document.topics.find((topic) => topic.code === code) ?? null)
          }}
        />
      )}

      {opened && document && (
        <TopicDrawer
          topic={opened}
          adding={adding}
          taken={document.topics.map((topic) => topic.code)}
          columns={columns}
          examinedIn={adding ? [] : examinedIn(document, columns, opened.code)}
          onSave={(topic, keys) => {
            setDraft(withMembership(withTopic(document, topic, adding), columns, topic.code, keys))
            setOpened(null)
          }}
          onRemove={
            adding
              ? null
              : () => {
                  setDraft(withoutTopic(document, opened.code))
                  setOpened(null)
                }
          }
          onClose={() => setOpened(null)}
        />
      )}
    </>
  )
}

/** The name the API gives the one Session of a Paper the Exam does not split. */
const DEFAULT_SESSION = 'Default'

/** The Topics and the Sections that examine them as a write takes them, always spelled out by Session. */
export type Document = {
  topics: Topic[]
  components: { componentCode: string; sections: { sectionCode: string; topics: string[] }[] }[]
}

/**
 * One Session of one Paper: P1 where the Exam does not split the Paper, and P2 Session 1
 * where it does.
 */
export type Column = { key: string; label: string; componentCode: string; sectionCode: string }

/** One Session as the screen lists it: its heading, and the Topics it examines in definition order. */
export type Section = Column & { paperName: string; topics: Topic[] }

export function columnsOf(view: TopicsView): Column[] {
  return view.components.flatMap((paper) =>
    paper.sections.map((session) => ({
      key: `${paper.componentCode}/${session.sectionCode}`,
      label:
        session.sectionCode === DEFAULT_SESSION ? paper.componentCode : `${paper.componentCode} ${session.sectionCode}`,
      componentCode: paper.componentCode,
      sectionCode: session.sectionCode,
    })),
  )
}

/**
 * A read as a write takes it: the Format's name, length and Question count dropped, because
 * they are the Format's answer and this is the document, and each Subtopic back to its text,
 * because its code is the API's to derive.
 */
export function documentOf(view: TopicsView): Document {
  return {
    topics: view.topics.map((topic) => ({
      code: topic.code,
      name: topic.name,
      detail: topic.detail,
      subtopics: topic.subtopics.map((subtopic) => subtopic.text),
    })),
    components: view.components.map((paper) => ({
      componentCode: paper.componentCode,
      sections: paper.sections.map((session) => ({ sectionCode: session.sectionCode, topics: session.topics })),
    })),
  }
}

/** Each Session with the Topics it examines, in the order the Topics are defined. */
export function sectionsOf(document: Document, columns: Column[], view?: TopicsView): Section[] {
  return columns.map((column) => {
    const codes = new Set((sessionOf(document, column)?.topics ?? []).map(lower))

    return {
      ...column,
      paperName: view?.components.find((paper) => paper.componentCode === column.componentCode)?.name ?? '',
      topics: document.topics.filter((topic) => codes.has(lower(topic.code))),
    }
  })
}

/** The Topics no Session examines, which the API refuses. */
export function unplacedOf(document: Document): Topic[] {
  const placed = new Set(
    document.components.flatMap((paper) => paper.sections.flatMap((session) => session.topics.map(lower))),
  )

  return document.topics.filter((topic) => !placed.has(lower(topic.code)))
}

/** The Sections that examine no Topic, which the API refuses too. */
export function emptyOf(document: Document, columns: Column[]): Column[] {
  return columns.filter((column) => (sessionOf(document, column)?.topics.length ?? 0) === 0)
}

/** The keys of the Sections that examine a Topic. */
export function examinedIn(document: Document, columns: Column[], code: string): string[] {
  return columns
    .filter((column) => sessionOf(document, column)?.topics.some((entry) => lower(entry) === lower(code)))
    .map((column) => column.key)
}

/**
 * The document with one Topic examined by exactly the Sections given: added where it was not,
 * dropped where it no longer is, and each Session kept in the order the Topics are defined.
 */
export function withMembership(document: Document, columns: Column[], code: string, keys: string[]): Document {
  const order = new Map(document.topics.map((topic, at) => [lower(topic.code), at]))
  const rank = (entry: string) => order.get(lower(entry)) ?? Number.MAX_SAFE_INTEGER

  return {
    ...document,
    components: document.components.map((paper) => ({
      ...paper,
      sections: paper.sections.map((session) => {
        const column = columns.find(
          (entry) => entry.componentCode === paper.componentCode && entry.sectionCode === session.sectionCode,
        )
        const kept = session.topics.filter((entry) => lower(entry) !== lower(code))
        const topics = column && keys.includes(column.key) ? [...kept, code] : kept

        return { ...session, topics: [...topics].sort((a, b) => rank(a) - rank(b)) }
      }),
    })),
  }
}

/** The document with a Topic added at the end, or edited where it stands. */
export function withTopic(document: Document, topic: Topic, adding: boolean): Document {
  return {
    ...document,
    topics: adding
      ? [...document.topics, topic]
      : document.topics.map((entry) => (entry.code === topic.code ? topic : entry)),
  }
}

/** The document without that Topic, and without it under any Session. */
export function withoutTopic(document: Document, code: string): Document {
  return {
    topics: document.topics.filter((topic) => topic.code !== code),
    components: document.components.map((paper) => ({
      ...paper,
      sections: paper.sections.map((session) => ({
        ...session,
        topics: session.topics.filter((entry) => lower(entry) !== lower(code)),
      })),
    })),
  }
}

function sessionOf(document: Document, column: Column) {
  return document.components
    .find((paper) => paper.componentCode === column.componentCode)
    ?.sections.find((session) => session.sectionCode === column.sectionCode)
}

/** A Topic Code is matched without regard to case, as the API matches one. */
function lower(code: string): string {
  return code.toLowerCase()
}

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-4 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
